import type { ApiError } from '@chikara/types';
import { APP_VERSION, isQaFrontendHost } from '@/config/version';
import { track } from './openpanel';
import { sanitizeText } from './errors';

/**
 * Instrumentación del onboarding por invitación para diagnóstico.
 *
 * Objetivo: saber si `acceptInvite` y `setupPassword` se llaman, cuántas veces
 * y con qué contexto, SIN exponer datos sensibles. No se registran tokens,
 * contraseñas, emails ni UUIDs: solo `iat`/`exp` del token (timestamps), el
 * conteo de llamadas, el estado de visibilidad/prerender y el código de error.
 */

// Id aleatorio por carga del documento JS. Permite correlacionar eventos del
// MISMO contexto: si vemos dos `accept_invite` con el mismo loadId => doble
// llamada en el mismo documento (StrictMode/listener); con loadId distinto =>
// documentos distintos (p. ej. el prerender del mail + la pestaña real).
const loadId = Math.random().toString(36).slice(2, 10);

let acceptInviteCount = 0;

/** Solo iat/exp del JWT (timestamps, NO sensible). Nunca el token. */
function tokenTimes(token?: string): { tokenIat?: number; tokenExp?: number } {
  if (!token) return {};
  try {
    const part = token.split('.')[1];
    if (!part) return {};
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return { tokenIat: json?.iat, tokenExp: json?.exp };
  } catch {
    return {};
  }
}

/** Busca el password_token sin asumir la profundidad del anidado (data.data…). */
function findPasswordToken(obj: unknown, depth = 0): string | undefined {
  if (!obj || typeof obj !== 'object' || depth > 6) return undefined;
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (key === 'password_token' && typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      const found = findPasswordToken(value, depth + 1);
      if (found) return found;
    }
  }
  return undefined;
}

function envContext(): Record<string, unknown> {
  const nav =
    typeof performance !== 'undefined'
      ? (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
      : undefined;
  return {
    loadId,
    appVersion: APP_VERSION,
    visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
    prerendering:
      typeof document !== 'undefined'
        ? Boolean((document as { prerendering?: boolean }).prerendering)
        : undefined,
    navType: nav?.type,
    hasReferrer: typeof document !== 'undefined' ? Boolean(document.referrer) : undefined,
  };
}

function errorBasics(err: unknown): Record<string, unknown> {
  const e = err as Partial<ApiError> | undefined;
  return {
    status: e?.status,
    code: e?.code,
    backendCode: e?.backend?.error?.code ?? e?.code,
    backendMessage: sanitizeText(e?.backendMessage ?? e?.backend?.error?.message, 200),
    requestId: e?.backend?.meta?.request_id,
  };
}

function emit(event: string, props: Record<string, unknown>): void {
  track(event, props);
  if (isQaFrontendHost()) {
    console.log(`[QA build v${APP_VERSION}] ${event}`, props);
  }
}

export function trackAcceptInviteCalled(): number {
  acceptInviteCount += 1;
  emit('onboarding_accept_invite_called', { ...envContext(), attempt: acceptInviteCount });
  return acceptInviteCount;
}

export function trackAcceptInviteResult(ok: boolean, result?: unknown, err?: unknown): void {
  const token = ok ? findPasswordToken(result) : undefined;
  emit('onboarding_accept_invite_result', {
    ...envContext(),
    attempt: acceptInviteCount,
    ok,
    ...tokenTimes(token),
    ...(ok ? {} : errorBasics(err)),
  });
}

export function trackSetupPasswordCalled(token?: string): void {
  emit('onboarding_setup_password_called', {
    ...envContext(),
    acceptInviteCount,
    ...tokenTimes(token),
  });
}

export function trackSetupPasswordResult(ok: boolean, token?: string, err?: unknown): void {
  emit('onboarding_setup_password_result', {
    ...envContext(),
    acceptInviteCount,
    ok,
    ...tokenTimes(token),
    ...(ok ? {} : errorBasics(err)),
  });
}
